import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/layout/Header";
import { Sidebar } from "@/components/layout/Sidebar";
import { Helmet } from "react-helmet-async";
import { Users, TrendingUp, CheckCircle, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export default function Communities() {
  const { data: communities, isLoading } = useQuery({
    queryKey: ["communities"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("communities")
        .select("*")
        .order("member_count", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data ?? [];
    },
  });

  const formatNumber = (num: number | null) => {
    if (!num) return "0";
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  return (
    <>
      <Helmet>
        <title>Communities - TheMoonRun</title>
        <meta name="description" content="Join trading communities on TheMoonRun. Trade together, share alpha, and grow your portfolio." />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Header />
        
        <div className="flex">
          <Sidebar />
          
          <main className="flex-1 min-w-0 p-4 lg:p-6">
            <div className="max-w-5xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Users className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-display font-bold text-foreground">Communities</h1>
                    <p className="text-muted-foreground">Join squads and trade together</p>
                  </div>
                </div>
                <Button variant="hero">
                  <Plus className="w-4 h-4" />
                  Create Community
                </Button>
              </div>

              {/* Community Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {isLoading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="p-5 rounded-xl bg-card border border-border">
                      <div className="flex items-center gap-3 mb-4">
                        <Skeleton className="w-12 h-12 rounded-xl" />
                        <div className="flex-1">
                          <Skeleton className="h-5 w-24 mb-1" />
                          <Skeleton className="h-4 w-16" />
                        </div>
                      </div>
                      <Skeleton className="h-4 w-full mb-3" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                  ))
                ) : communities && communities.length > 0 ? (
                  communities.map((community) => (
                    <div
                      key={community.id}
                      className={cn(
                        "p-5 rounded-xl bg-card border transition-all hover:border-primary/50 hover-lift",
                        community.is_verified ? "border-primary/30" : "border-border"
                      )}
                    >
                      <div className="flex items-start gap-3 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-secondary overflow-hidden flex-shrink-0">
                          {community.image ? (
                            <img src={community.image} alt={community.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-lg font-bold text-primary">
                              {community.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-foreground truncate">{community.name}</h3>
                            {community.is_verified && (
                              <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                            )}
                          </div>
                          {community.tag && (
                            <Badge variant="secondary" className="text-xs mt-1">{community.tag}</Badge>
                          )}
                        </div>
                      </div>

                      <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                        {community.description || "No description"}
                      </p>

                      <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                        <div className="p-2 rounded-lg bg-secondary/50">
                          <p className="text-xs text-muted-foreground">Members</p>
                          <p className="font-mono font-semibold text-foreground">
                            {formatNumber(community.member_count)}
                          </p>
                        </div>
                        <div className="p-2 rounded-lg bg-secondary/50">
                          <p className="text-xs text-muted-foreground">Volume</p>
                          <p className="font-mono font-semibold text-foreground">
                            {formatNumber(community.total_volume_sol)} SOL
                          </p>
                        </div>
                        <div className="p-2 rounded-lg bg-secondary/50">
                          <p className="text-xs text-muted-foreground">Profit</p>
                          <p className={cn(
                            "font-mono font-semibold",
                            (community.total_profit_sol ?? 0) >= 0 ? "text-success" : "text-destructive"
                          )}>
                            {(community.total_profit_sol ?? 0) >= 0 ? "+" : ""}{formatNumber(community.total_profit_sol)}
                          </p>
                        </div>
                      </div>

                      <Button variant="outline" className="w-full">
                        <Users className="w-4 h-4" />
                        Join Community
                      </Button>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full p-12 text-center">
                    <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-muted-foreground">No communities yet</p>
                    <p className="text-sm text-muted-foreground mb-4">Be the first to create a trading community!</p>
                    <Button variant="hero">
                      <Plus className="w-4 h-4" />
                      Create Community
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
